export type OpenReplayTracker = {
  start: () => void | Promise<{ success: boolean }>;
  configure?: (config: Record<string, unknown>) => void;
  use?: (plugin: unknown) => void;
  setUserID?: (userId: string) => void;
  setMetadata?: (key: string, value: string) => void;
  event?: (name: string, payload?: any) => void;
  handleError?: (e: any, metadata?: Record<string, any>) => void;
  /**
   * session 真正建立前返回 null/undefined（SDK：`app === null` → null；
   * session 未开始 → `sessionID || undefined`）。所以读它必须轮询，
   * 见 `src/lib/tracking/nativeSession.ts`。
   */
  getSessionID?: () => string | null | undefined;
};

import log from "logbeacon";

declare global {
  interface Window {
    __OR_TRACKER__?: OpenReplayTracker | null;
    __OR_TRACKER_READY__?: Promise<OpenReplayTracker | null> | null;
  }
}

const pendingEvents: Array<{
  name: string;
  payload?: Record<string, unknown>;
}> = [];
const MAX_PENDING_EVENTS = 100;
const readyListeners = new Set<() => void>();

export function onOpenReplayTrackerReady(listener: () => void): () => void {
  if (getTracker()) {
    listener();
  } else {
    readyListeners.add(listener);
  }
  return () => {
    readyListeners.delete(listener);
  };
}

function flushPendingEvents(tracker: OpenReplayTracker): void {
  const batch = pendingEvents.splice(0);
  for (const event of batch) {
    try {
      tracker.event?.(event.name, event.payload);
    } catch {
      // One SDK failure must not discard the remaining batch.
    }
  }
}

export function getOrCreateClientUuid(): string {
  // Use the project's canonical uuid source (logbeacon).
  // NOTE: current codebase exposes `log.getOrCreateUUID()` (used for fingerprint init).
  return (log as any).getOrCreateClientUuid?.() ?? log.getOrCreateUUID();
}

export function getTracker(): OpenReplayTracker | null {
  return window.__OR_TRACKER__ ?? null;
}

export async function ensureOpenReplayTrackerStarted(): Promise<OpenReplayTracker | null> {
  if (typeof window === "undefined") {
    return null;
  }

  const existing = getTracker();
  if (existing) {
    return existing;
  }

  if (window.__OR_TRACKER_READY__) {
    return window.__OR_TRACKER_READY__;
  }

  window.__OR_TRACKER_READY__ = (async () => {
    try {
      const projectKey = process.env.NEXT_PUBLIC_OPENREPLAY_PROJECT_KEY;
      const ingestPoint = process.env.NEXT_PUBLIC_OPENREPLAY_INGEST_POINT;
      if (!projectKey) {
        return null;
      }

      const [tracker, trackerAssist] = await Promise.all([
        import("@openreplay/tracker").then(
          (m) => m.tracker as unknown as OpenReplayTracker,
        ),
        import("@openreplay/tracker-assist").then((m) => m.default),
      ]);

      // consoleMethods：console 自动采集的统一开关，见 `src/lib/tracking/config.ts`。
      // 迁移期开着（存量 log.* 经 console 自动进 OR，零代码兜底）；
      // 存量迁到 Track.log 后关掉，避免与结构化 log 事件双通路 + 三方库噪音。
      const { consoleMethodsOption } = await import(
        "@/src/lib/tracking/config"
      );
      tracker.configure?.({
        projectKey: projectKey,
        ingestPoint: ingestPoint,
        __DISABLE_SECURE_MODE: process.env.NODE_ENV !== "production",
        consoleMethods: consoleMethodsOption(),
      });

      try {
        tracker.use?.((trackerAssist as any)({}));
      } catch {
        // ignore plugin failures
      }

      const started = await tracker.start();
      if (started && !started.success) {
        return null;
      }
      window.__OR_TRACKER__ = tracker;

      // Anonymous user continuity (before login).
      try {
        const clientUuid = getOrCreateClientUuid();
        tracker.setUserID?.(clientUuid);
      } catch {
        // ignore
      }

      // Error reporting (best-effort)
      window.addEventListener("error", (e) => {
        (tracker as any).handleError?.(e);
      });
      window.addEventListener("unhandledrejection", (e) => {
        (tracker as any).handleError?.(e);
      });

      for (const listener of [...readyListeners]) {
        readyListeners.delete(listener);
        try {
          listener();
        } catch {
          /* Tracking must not block startup. */
        }
      }
      flushPendingEvents(tracker);
      return tracker;
    } catch {
      return null;
    }
  })();

  const pending = window.__OR_TRACKER_READY__;
  const tracker = await pending;
  if (!tracker && window.__OR_TRACKER_READY__ === pending) {
    pendingEvents.splice(0);
    window.__OR_TRACKER_READY__ = null;
  }
  return tracker;
}

/**
 * Best-effort OpenReplay custom event (shows up in OpenReplay analytics).
 * Safe to call before tracker is ready.
 */
export function emitOpenReplayEvent(
  name: string,
  payload?: Record<string, unknown>,
): void {
  if (typeof window === "undefined") {
    return;
  }
  try {
    const tracker = getTracker();
    if (tracker) {
      tracker.event?.(name, payload);
    } else if (process.env.NEXT_PUBLIC_OPENREPLAY_PROJECT_KEY) {
      if (pendingEvents.length >= MAX_PENDING_EVENTS) {
        pendingEvents.shift();
      }
      pendingEvents.push({
        name,
        payload: payload ? { ...payload } : undefined,
      });
    }
  } catch {
    // ignore
  }
}

/**
 * 点击 label：`seg_seg_…` —— 全小写、下划线分隔、**1–9 段**。
 *
 * 规范 §3.3。与 KMP `buildOpenReplayLabel` **逐字一致**，改一端必须改另一端；
 * 段数上下限的唯一来源是 `scripts/lib/label-format.mjs`。
 *
 * ## 段数为什么不锁死三段
 *
 * 层级不是恒定三层。锁死 `scene_domain_action` 时，同一 domain 下的两个按钮
 * （Photo / Files）要么共用一个名字、要么把区分度硬塞成 `selectphoto` 连写词。
 * 深的地方多写一段即可：`chat_attachment_select_photo`。
 *
 * ## 每段必须是有界枚举
 *
 * 放开段数**不等于**可以往名字里塞变量。原文案、列表项 id、资产代码一律不进 label：
 * - **回放里本来就看得见** —— OpenReplay 录的是 DOM，按钮上写什么点开就知道
 * - **它不稳定** —— i18n 切语言、文案 A/B 一改，label 就变成另一个值，聚合直接断
 * - **基数会爆** —— 每个列表项一个名字，等于没有名字
 *
 * 要带变量就发一条有业务含义的事件：`Track.event("brief_card_open", { card_id })`。
 *
 * ## 为什么是 `_`
 *
 * 不用转义（正则/CSV/URL/JSON 里都是普通字符）；判据统一成
 * 「**下划线 = 我们有意要采的**」，其余是 SDK 兜底抓到的噪音（innerText / class 串）。
 */
export function buildOpenReplayLabel(...segments: string[]): string {
  return segments
    .map((x) => x.replace(/[^A-Za-z0-9]/g, "").toLowerCase())
    .join("_");
}
